import { z } from 'zod'

export type CdekPrintKind = 'a6' | 'a4'

export interface CdekPrintAgentTarget {
  printerName: string
  media: 'A6' | 'A4'
}

export interface CdekPrintAgentTargets {
  a6: CdekPrintAgentTarget
  a4: CdekPrintAgentTarget
}

export interface CdekPrintAgentStatus {
  version: 'cdek-print-agent-v1'
  targets: CdekPrintAgentTargets
}

export type CdekPrintAgentFailure = 'unavailable' | 'refused' | 'invalid_response' | 'unknown'

export class CdekPrintAgentError extends Error {
  readonly kind: CdekPrintAgentFailure
  readonly status: number | null

  constructor(kind: CdekPrintAgentFailure, message: string, status: number | null = null) {
    super(message)
    this.name = 'CdekPrintAgentError'
    this.kind = kind
    this.status = status
  }
}

export const CDEK_PRINT_AGENT_URL = 'http://127.0.0.1:8765'
export const CDEK_PRINT_AGENT_ENABLED_KEY = 'almaz.cdekPrintAgent'

/** The local agent is opt-in per workstation; everywhere else printing is the browser's PDF dialog. */
export function isCdekPrintAgentEnabled(): boolean {
  try {
    return window.localStorage.getItem(CDEK_PRINT_AGENT_ENABLED_KEY) === 'enabled'
  } catch {
    return false
  }
}

const AgentTargetSchema = z.object({
  printerName: z.string().min(1).max(240),
  media: z.enum(['A6', 'A4']),
}).strict()

const AgentStatusSchema = z.object({
  version: z.literal('cdek-print-agent-v1'),
  targets: z.object({
    a6: AgentTargetSchema,
    a4: AgentTargetSchema,
  }).strict(),
}).strict()

const AgentPrintResponseSchema = z.object({
  status: z.literal('printed'),
  kind: z.enum(['a6', 'a4']),
  printerName: z.string().min(1).max(240),
  media: z.enum(['A6', 'A4']),
}).strict()

const AgentRefusalSchema = z.object({
  error: z.string().min(1).max(80),
  reason: z.string().min(1).max(80).optional(),
  message: z.string().min(1).max(240).optional(),
}).strict()

function agentUrl(path: string): string {
  return `${CDEK_PRINT_AGENT_URL}${path}`
}

function withTimeout(): { signal: AbortSignal; cancel: () => void } {
  const controller = new AbortController()
  const timer = window.setTimeout(() => controller.abort(), 1500)
  return { signal: controller.signal, cancel: () => window.clearTimeout(timer) }
}

async function responseJson(response: Response): Promise<unknown> {
  try {
    return await response.json()
  } catch {
    return null
  }
}

function refusalMessage(payload: unknown): string {
  const parsed = AgentRefusalSchema.safeParse(payload)
  if (!parsed.success) return 'Локальный агент печати вернул непонятный ответ.'
  return parsed.data.message || 'Локальный агент печати отказал в выполнении.'
}

export function createCdekPrintRequestId(): string {
  return crypto.randomUUID()
}

export function cdekPrintAgentStatusFailureMessage(): string {
  return 'Не удалось связаться с локальным агентом печати. Проверьте, что программа запущена и браузер разрешил этому сайту доступ к локальной сети. Используем печать в браузере.'
}

export async function fetchCdekPrintAgentStatus(): Promise<CdekPrintAgentStatus> {
  const timeout = withTimeout()
  let response: Response
  try {
    response = await fetch(agentUrl('/v1/status'), {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal: timeout.signal,
    })
  } catch (caught) {
    throw new CdekPrintAgentError(
      'unavailable',
      caught instanceof DOMException && caught.name === 'AbortError'
        ? 'Локальный агент печати не ответил вовремя.'
        : 'Локальный агент печати недоступен.',
    )
  } finally {
    timeout.cancel()
  }
  if (!response.ok) {
    throw new CdekPrintAgentError('unavailable', 'Локальный агент печати недоступен.', response.status)
  }
  const parsed = AgentStatusSchema.safeParse(await responseJson(response))
  if (!parsed.success || parsed.data.targets.a6.media !== 'A6' || parsed.data.targets.a4.media !== 'A4') {
    throw new CdekPrintAgentError('invalid_response', 'Локальный агент печати вернул неверный статус.', response.status)
  }
  return parsed.data
}

export async function printCdekDocumentWithAgent(
  kind: CdekPrintKind,
  pdf: Blob,
  requestId: string = createCdekPrintRequestId(),
): Promise<CdekPrintAgentTarget> {
  const timeout = withTimeout()
  let response: Response
  try {
    response = await fetch(agentUrl(`/v1/print/${kind}`), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/pdf',
        'X-Cdek-Print-Request-Id': requestId,
      },
      body: pdf,
      signal: timeout.signal,
    })
  } catch {
    throw new CdekPrintAgentError(
      'unknown',
      'Не удалось подтвердить печать — проверьте принтер.',
    )
  } finally {
    timeout.cancel()
  }
  const payload = await responseJson(response)
  if (!response.ok) {
    const parsed = AgentRefusalSchema.safeParse(payload)
    if (!parsed.success) {
      throw new CdekPrintAgentError(
        'unknown',
        'Не удалось подтвердить печать — проверьте принтер.',
        response.status,
      )
    }
    throw new CdekPrintAgentError('refused', refusalMessage(parsed.data), response.status)
  }
  const parsed = AgentPrintResponseSchema.safeParse(payload)
  const expectedMedia = kind === 'a6' ? 'A6' : 'A4'
  if (!parsed.success || parsed.data.kind !== kind || parsed.data.media !== expectedMedia) {
    throw new CdekPrintAgentError(
      'unknown',
      'Не удалось подтвердить печать — проверьте принтер.',
      response.status,
    )
  }
  return { printerName: parsed.data.printerName, media: parsed.data.media }
}

export function cdekPrintAgentFailureMessage(error: CdekPrintAgentError): string {
  if (error.kind === 'unknown') return `${error.message} Повторную печать не запускаем автоматически.`
  return `${error.message} Открываем печать в браузере.`
}
